import type { ThemeOptions } from "@mui/material/styles";
import { darkSurface, neutral, RADIUS } from "./tokens";

/**
 * Global component styling. Everything here used to be repeated by hand in
 * per-component SCSS files, which is why buttons, inputs and cards each had
 * three or four slightly different looks across the app.
 */
export const components: ThemeOptions["components"] = {
  MuiCssBaseline: {
    styleOverrides: {
      "*, *::before, *::after": { boxSizing: "border-box" },
      html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
      body: { minHeight: "100vh" },
      // Number inputs across the app are used for phone numbers and IDs, where
      // the spinner is never wanted.
      'input[type="number"]': { MozAppearance: "textfield" },
      "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
      },
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          transitionDuration: "0.01ms !important",
        },
      },
    },
  },

  MuiButtonBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI's default focus treatment is a ripple, which leaves keyboard
        // users with no visible target on non-rippling surfaces.
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }),
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => [
        { borderRadius: RADIUS.sm, paddingInline: 16, whiteSpace: "nowrap" },
        // Text and outlined buttons draw the brand colour *as* text, so in dark
        // mode they need `primary.light`; only contained buttons, which fill
        // with `primary.main` and print white on top, want `main`.
        theme.applyStyles("dark", {
          "&.MuiButton-text.MuiButton-colorPrimary, &.MuiButton-outlined.MuiButton-colorPrimary":
            {
              color: theme.palette.primary.light,
            },
          "&.MuiButton-outlined.MuiButton-colorPrimary": {
            borderColor: theme.palette.primary.main,
          },
        }),
      ],
      sizeSmall: { paddingInline: 12 },
      sizeLarge: { paddingInline: 22, paddingBlock: 10 },
    },
  },

  MuiIconButton: {
    styleOverrides: { root: { borderRadius: RADIUS.sm } },
  },

  MuiTextField: {
    defaultProps: { size: "small", variant: "outlined" },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      // Deliberately transparent. A shrunk label straddles the top border, so
      // its upper half paints over whatever contains the field and its lower
      // half over the field itself; any opaque fill that differs from the
      // container slices the label in two. Affordance comes from the outline,
      // which the stronger `divider` now makes clearly visible.
      root: { borderRadius: RADIUS.sm, backgroundColor: "transparent" },
      // Autofill paints only the `input` element, so on a field with
      // adornments the browser's fill stops short of the outline at both ends
      // and reads as a stripe rather than a filled field. An inset shadow is
      // the only thing that reliably overrides that paint (Safari ignores the
      // transition-delay trick), so match it to the surface the fields sit on
      // and the fill disappears instead of being made to span the field.
      input: ({ theme }) => ({
        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active":
          {
            WebkitBoxShadow: `0 0 0 100px ${theme.vars.palette.background.paper} inset`,
            WebkitTextFillColor: theme.vars.palette.text.primary,
            caretColor: theme.vars.palette.text.primary,
          },
      }),
    },
  },

  MuiSelect: { defaultProps: { size: "small" } },
  MuiFormControl: { defaultProps: { size: "small" } },

  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" },
      rounded: { borderRadius: RADIUS.md },
    },
  },

  // Anything floating above the page needs to be lighter than the cards it
  // covers; dark-mode shadows carry no depth information on their own.
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) =>
        theme.applyStyles("dark", {
          backgroundColor: darkSurface.overlay,
          border: `1px solid ${theme.palette.divider}`,
        }),
    },
  },

  MuiAutocomplete: {
    defaultProps: { size: "small" },
    styleOverrides: {
      paper: ({ theme }) =>
        theme.applyStyles("dark", {
          backgroundColor: darkSurface.overlay,
          border: `1px solid ${theme.palette.divider}`,
        }),
    },
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: RADIUS.md,
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiCardHeader: {
    defaultProps: {
      slotProps: {
        title: { variant: "h5" },
        subheader: { variant: "body2" },
      },
    },
    styleOverrides: { root: { padding: 20 } },
  },

  MuiCardContent: {
    styleOverrides: { root: { padding: 20, "&:last-child": { paddingBottom: 20 } } },
  },

  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => [
        { borderRadius: RADIUS.lg },
        theme.applyStyles("dark", {
          backgroundColor: darkSurface.overlay,
          border: `1px solid ${theme.palette.divider}`,
        }),
      ],
    },
  },

  MuiDialogTitle: {
    styleOverrides: { root: { fontSize: "1.125rem", fontWeight: 600, padding: "20px 24px 8px" } },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        // MUI drops the top padding to zero whenever the content follows a
        // title, which scroll-clips the floating label of a leading text field
        // (the label sits 9px above its input).
        paddingTop: 8,
        paddingBottom: 20,
        ".MuiDialogTitle-root + &": { paddingTop: 8 },
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: { root: { padding: "12px 24px 20px", gap: 8 } },
  },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: RADIUS.sm, fontWeight: 600 },
      sizeSmall: { height: 22, fontSize: "0.75rem" },
    },
  },

  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: { tooltip: { fontSize: "0.75rem", borderRadius: RADIUS.sm, padding: "6px 10px" } },
  },

  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => [
        { textTransform: "none", fontWeight: 600, minHeight: 48, fontSize: "0.875rem" },
        // See the note on `primary` in theme/palette.ts: `main` is sized for
        // white-on-blue fills, `light` for blue-on-dark text.
        theme.applyStyles("dark", {
          "&.Mui-selected": { color: theme.palette.primary.light },
        }),
      ],
    },
  },

  MuiTableCell: {
    styleOverrides: {
      head: ({ theme }) => [
        {
          fontWeight: 600,
          backgroundColor: neutral[100],
          color: theme.palette.text.secondary,
        },
        // Header bands lift above the surface they sit on; in dark mode that
        // means going lighter, not reaching for the page background.
        theme.applyStyles("dark", { backgroundColor: darkSurface.raised }),
      ],
    },
  },

  MuiListItemButton: {
    styleOverrides: { root: { borderRadius: RADIUS.sm } },
  },

  MuiListItemIcon: {
    styleOverrides: { root: { minWidth: 40 } },
  },

  MuiAlert: {
    styleOverrides: { root: { borderRadius: RADIUS.sm, alignItems: "center" } },
  },

  MuiLink: {
    defaultProps: { underline: "hover" },
    styleOverrides: {
      root: ({ theme }) =>
        theme.applyStyles("dark", { color: theme.palette.primary.light }),
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme }) =>
        theme.applyStyles("dark", {
          backgroundColor: theme.palette.primary.light,
        }),
    },
  },

  MuiSkeleton: {
    defaultProps: { animation: "wave" },
  },

  // Surfaces, borders, row hover and muted text all come from
  // `palette.DataGrid` plus `divider`, `action.hover` and `text.secondary`,
  // which the grid maps onto its own CSS variables. Only the things it has no
  // token for belong here.
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => [
        {
          borderRadius: RADIUS.md,
          border: `1px solid ${theme.palette.divider}`,
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
        },
        theme.applyStyles("dark", {
          // Defaults to `primary.dark`, which is near-invisible on a dark grid.
          "--DataGrid-t-color-foreground-accent": theme.palette.primary.light,
        }),
      ],
    },
  },
};
